/** Which source a display session reads from.
 *
 *  A Reactivate till launches the display with a receiver grant in the URL
 *  fragment (`#receiver_token=…`). That session belongs to the device-request
 *  socket for its whole life. A launch without one is a legacy till and stays
 *  on Firebase. `token` is null when the session was paired but the grant is
 *  gone, e.g. after a reload: it must wait for the till to pair it again, and
 *  must never fall back to Firebase on the strength of the query ids alone. */
export type DisplaySession = { kind: 'legacy' } | { kind: 'device_requests'; token: string | null }

/** Grants are 32 random bytes, base64url without padding. */
const TOKEN = /^[A-Za-z0-9_-]{43}$/

/** Remembered in history state, not storage: it survives a reload of this tab
 *  and nothing else, and it is not a secret. */
const SESSION_MARK = 'cfd_source'

export function readDisplaySession(location: Location = window.location, history: History = window.history): DisplaySession {
  const fragment = new URLSearchParams(location.hash.slice(1))
  const token = fragment.get('receiver_token')

  if (token !== null) {
    // Take the grant out of the address bar straight away, so it never shows
    // up in a screenshot, a bookmark or an error report's URL.
    fragment.delete('receiver_token')
    const rest = fragment.toString()
    try {
      history.replaceState(
        { ...(history.state as object | null), [SESSION_MARK]: 'device_requests' },
        '',
        `${location.pathname}${location.search}${rest ? `#${rest}` : ''}`,
      )
    } catch {
      // A WebView that refuses replaceState still gets the session.
    }
    return { kind: 'device_requests', token: TOKEN.test(token) ? token : null }
  }

  const state = history.state as Record<string, unknown> | null
  if (state && state[SESSION_MARK] === 'device_requests') return { kind: 'device_requests', token: null }

  return { kind: 'legacy' }
}

let pageSession: DisplaySession | undefined

/** This page's session, read once. Reading strips the grant from the URL, so
 *  a second read (React StrictMode renders twice) would find it gone. */
export function displaySession(): DisplaySession {
  if (!pageSession) pageSession = readDisplaySession()
  return pageSession
}

/** The till renews a grant before it expires by showing the same URL with a
 *  new fragment. A WebView treats that as a hash change, not a page load, so
 *  without this the display would hold on to the old grant until it is
 *  refused. A reload reads the new grant fresh and restores the screen. */
export function reloadOnNewGrant(target: Window = window): () => void {
  const onHashChange = () => {
    if (new URLSearchParams(target.location.hash.slice(1)).has('receiver_token')) target.location.reload()
  }
  target.addEventListener('hashchange', onHashChange)
  return () => target.removeEventListener('hashchange', onHashChange)
}
